import {
  TIAN_GAN,
  DI_ZHI,
  ELEMENTS,
  SHI_SHEN_MAP,
  NA_YIN,
  SHEN_SHA_RULES,
  Stem,
  Branch,
} from './baziData'

export interface Pillar {
  stem: Stem
  branch: Branch
  naYin: string
  shiShen: string
  hiddenStems: { stem: Stem; shiShen: string }[]
  shenSha: string[]
}

export interface BaZiChart {
  year: Pillar
  month: Pillar
  day: Pillar
  hour: Pillar
  dayMaster: Stem
  voidBranches: string[]
  elementCounts: Record<string, number>
}

// Helper to get stem/branch by index
const getStem = (index: number) => TIAN_GAN[((index % 10) + 10) % 10]
const getBranch = (index: number) => DI_ZHI[((index % 12) + 12) % 12]

// Calculate Ten Gods (Shi Shen)
export const getShiShen = (dayMaster: Stem, target: Stem): string => {
  if (dayMaster.name === target.name) return '比肩'

  const isSameYinYang = dayMaster.yinYang === target.yinYang
  const dmElement = ELEMENTS[dayMaster.element]
  const targetElement = ELEMENTS[target.element]

  let relation = ''
  if (dmElement.name === targetElement.name) relation = 'same'
  else if (dmElement.generates === target.element) relation = 'gen'
  else if (dmElement.controls === target.element) relation = 'ctrl'
  else if (targetElement.controls === dayMaster.element) relation = 'ctrled'
  else if (targetElement.generates === dayMaster.element) relation = 'gened'

  const key = `${relation}_${isSameYinYang ? 'same' : 'diff'}`
  return SHI_SHEN_MAP[key] || ''
}

// Simplified Solar Term boundaries for month calculation (approximate 4th-8th of each month)
// In a real app, this requires complex astronomical calculations.
const getMonthBranchIndex = (month: number, day: number): number => {
  // Approximate JieQi dates (usually 4th-8th)
  const jieQiDays = [0, 5, 4, 5, 4, 5, 5, 7, 7, 7, 8, 7, 7]
  let m = month
  if (day < jieQiDays[month]) {
    m -= 1
  }
  if (m <= 0) m += 12
  // Yin (寅) is the 1st month of the BaZi year (starts around Feb 4)
  // Index of Yin is 2. So month 1 (Feb) -> 2.
  return (m + 1) % 12
}

// 五虎遁 (Five Tigers) for Month Stem
const getMonthStemIndex = (
  yearStemIndex: number,
  monthBranchIndex: number,
): number => {
  // 甲己之年丙作首...
  const baseStems = [2, 4, 6, 8, 0] // 丙, 戊, 庚, 壬, 甲
  const base = baseStems[yearStemIndex % 5]
  // monthBranchIndex for Yin(寅) is 2. We want Yin to be offset 0.
  const offset = (monthBranchIndex - 2 + 12) % 12
  return (base + offset) % 10
}

// 五鼠遁 (Five Rats) for Hour Stem
const getHourStemIndex = (
  dayStemIndex: number,
  hourBranchIndex: number,
): number => {
  // 甲己还加甲...
  const baseStems = [0, 2, 4, 6, 8] // 甲, 丙, 戊, 庚, 壬
  const base = baseStems[dayStemIndex % 5]
  return (base + hourBranchIndex) % 10
}

// Calculate Void Branches (空亡)
const getVoidBranches = (
  dayStemIndex: number,
  dayBranchIndex: number,
): string[] => {
  const groupOffset = (dayBranchIndex - dayStemIndex + 12) % 12
  // The void branches are the two branches before the group start
  const void1 = getBranch(groupOffset - 2).name
  const void2 = getBranch(groupOffset - 1).name
  return [void1, void2]
}

// Main Generation Function
export const generateFullChart = (
  year: number,
  month: number,
  day: number,
  hour: number,
  gender: 'M' | 'F',
): BaZiChart => {
  // 1. Year Pillar (1984 is 甲子, index 0)
  const yearOffset = year - 1984
  const yearStemIndex = ((yearOffset % 10) + 10) % 10
  const yearBranchIndex = ((yearOffset % 12) + 12) % 12

  // 2. Month Pillar
  const monthBranchIndex = getMonthBranchIndex(month, day)
  const monthStemIndex = getMonthStemIndex(yearStemIndex, monthBranchIndex)

  // 3. Day Pillar (Using 1970-01-01 as epoch: 丁亥 Ding Hai, Stem 3, Branch 11)
  const date = new Date(year, month - 1, day)
  const offsetDays = Math.floor(
    (date.getTime() - date.getTimezoneOffset() * 60000) / 86400000,
  )
  const epochStem = 3
  const epochBranch = 11
  const dayStemIndex = (epochStem + (offsetDays % 10) + 10) % 10
  const dayBranchIndex = (epochBranch + (offsetDays % 12) + 12) % 12

  // 4. Hour Pillar
  // 23-1 is 子(0), 1-3 is 丑(1), etc.
  const hourBranchIndex = Math.floor((hour + 1) / 2) % 12
  const hourStemIndex = getHourStemIndex(dayStemIndex, hourBranchIndex)

  const dayMaster = getStem(dayStemIndex)

  const buildPillar = (sIdx: number, bIdx: number): Pillar => {
    const stem = getStem(sIdx)
    const branch = getBranch(bIdx)
    const jiaZi = `${stem.name}${branch.name}`

    // Hidden Stems & their Ten Gods
    const hiddenStems = branch.hiddenStems.map((hsName) => {
      const hsStem = TIAN_GAN.find((s) => s.name === hsName)!
      return {
        stem: hsStem,
        shiShen: getShiShen(dayMaster, hsStem),
      }
    })

    // Shen Sha (Simplified)
    const shenSha: string[] = []
    if (SHEN_SHA_RULES.tianYi[dayMaster.name]?.includes(branch.name))
      shenSha.push('天乙贵人')
    if (SHEN_SHA_RULES.yiMa[getBranch(dayBranchIndex).name] === branch.name)
      shenSha.push('驿马')
    if (SHEN_SHA_RULES.taoHua[getBranch(dayBranchIndex).name] === branch.name)
      shenSha.push('桃花')
    if (SHEN_SHA_RULES.huaGai[getBranch(dayBranchIndex).name] === branch.name)
      shenSha.push('华盖')
    if (SHEN_SHA_RULES.yangRen[dayMaster.name] === branch.name)
      shenSha.push('羊刃')
    if (SHEN_SHA_RULES.luShen[dayMaster.name] === branch.name)
      shenSha.push('禄神')

    return {
      stem,
      branch,
      naYin: NA_YIN[jiaZi] || '未知',
      shiShen: getShiShen(dayMaster, stem),
      hiddenStems,
      shenSha,
    }
  }

  const chart: BaZiChart = {
    year: buildPillar(yearStemIndex, yearBranchIndex),
    month: buildPillar(monthStemIndex, monthBranchIndex),
    day: buildPillar(dayStemIndex, dayBranchIndex),
    hour: buildPillar(hourStemIndex, hourBranchIndex),
    dayMaster,
    voidBranches: getVoidBranches(dayStemIndex, dayBranchIndex),
    elementCounts: { wood: 0, fire: 0, earth: 0, metal: 0, water: 0 },
  }

  // Count Elements
  const countElement = (el: string) => {
    chart.elementCounts[el] = (chart.elementCounts[el] || 0) + 1
  }
  ;[chart.year, chart.month, chart.day, chart.hour].forEach((p) => {
    countElement(p.stem.element)
    countElement(p.branch.element)
    p.hiddenStems.forEach((hs) => countElement(hs.stem.element))
  })

  return chart
}
